/**
 * Synthetic per-building energy model.
 *
 * Stand-in for BEMS until the partnership lands. Same shape as what BEMS
 * will return, so swapping the source later means changing only this file
 * (or replacing it with a server adapter call).
 *
 * Inputs are publicly observable: OSM building type, levels, footprint area.
 * Mixed with hour-of-day + day-of-week heuristics calibrated against
 * typical Bangkok institutional energy intensity (W/m²), then perturbed
 * by a deterministic seed-derived noise so it animates but doesn't churn.
 *
 * Anomalies — 0.7% of buildings per refresh — render as red callouts on
 * the heatmap. That's the "stuck AC / leak / lights left on" moment a
 * facilities officer would action.
 */

import type { Feature, Polygon, MultiPolygon } from "geojson";

export interface BuildingProperties {
  id?: string;
  name?: string | null;
  nameEn?: string | null;
  nameTh?: string | null;
  building?: string | null;     // OSM building tag: university / hospital / yes / ...
  levels?: number | null;
  height?: number | null;
  operator?: string | null;
}

export interface BuildingEnergyReading {
  buildingId: string;
  name: string | null;
  buildingType: string;
  floors: number;
  floorAreaM2: number;
  currentKw: number;
  dailyKwh: number;
  /** Ratio versus this building's typical-hour baseline. 1.0 = normal. */
  vsAvg: number;
  /** 0..1 anomaly score. >0.7 surfaces in the panel. */
  anomalyScore: number;
  flags: string[];
}

// Peak energy intensity W/m² per OSM building category. Chosen against
// Thai institutional benchmarks (the academic Thai BEC ranges).
const INTENSITY_W_PER_M2: Record<string, number> = {
  hospital:       65,   // 24/7 ops + medical equipment + cooling
  laboratory:     55,
  research:       55,
  university:     35,
  school:         28,
  dormitory:      18,
  residential:    14,
  retail:         40,
  commercial:     45,
  office:         38,
  parking:        8,
  gymnasium:      22,
  sports_centre:  22,
  warehouse:      6,
  service:        10,
  yes:            28,   // OSM default
  default:        28,
};

function intensityFor(type: string | null | undefined): number {
  if (!type) return INTENSITY_W_PER_M2.default;
  return INTENSITY_W_PER_M2[type.toLowerCase()] ?? INTENSITY_W_PER_M2.default;
}

// ─── Pratumwan EUI overrides ──────────────────────────────────────────────
// Real-world ground truth from Thai building studies. EUI (kWh/m²/year) →
// W/m² average by dividing by 8760 hours/year × 1000. The hour/day
// multipliers in this file shape the diurnal curve around this average.
const HOURS_PER_YEAR = 8760;
const PRATUMWAN_AVG_W_PER_M2: Record<string, number> = {
  mall:        Math.round((270 / HOURS_PER_YEAR) * 1000),  // ≈31 — Siam Paragon ground truth
  office:      Math.round((200 / HOURS_PER_YEAR) * 1000),  // ≈23 — JARS Thai Grade-A office benchmark
  hotel:       Math.round((340 / HOURS_PER_YEAR) * 1000),  // ≈39 — 5-star with 24/7 HVAC + hot water
  hospital:    Math.round((420 / HOURS_PER_YEAR) * 1000),  // ≈48 — KCMH-class tertiary hospital
  condo:       Math.round(( 60 / HOURS_PER_YEAR) * 1000),  // ≈ 7 — Bangkok metered residential
  university:  Math.round((130 / HOURS_PER_YEAR) * 1000),  // ≈15 — CU mixed academic average
};

export interface PratumwanLandmarkRecord {
  id: string;
  name: string;
  nameTh: string;
  category: "mall" | "office" | "hotel" | "hospital" | "condo" | "university";
  heightM: number;
  gfaM2: number;
  euiKwhM2Yr: number;
  hasSTP: boolean;
  operator: string;
}

/** Match a building feature against a known Pratumwan landmark by name. */
export function lookupLandmark(
  feature: Feature<Polygon | MultiPolygon, BuildingProperties>,
  landmarkIndex: Map<string, PratumwanLandmarkRecord>,
): PratumwanLandmarkRecord | undefined {
  if (!landmarkIndex.size) return undefined;
  const names: string[] = [];
  const p = feature.properties ?? {};
  if (p.name) names.push(p.name.toLowerCase().trim());
  if (p.nameEn) names.push(p.nameEn.toLowerCase().trim());
  if (p.nameTh) names.push(p.nameTh.toLowerCase().trim());
  for (const n of names) {
    const hit = landmarkIndex.get(n);
    if (hit) return hit;
    // Loose match — landmark name is a prefix of the building's OSM name
    for (const [k, v] of landmarkIndex) {
      if (k.length >= 6 && n.startsWith(k)) return v;
      if (k.length >= 6 && k.startsWith(n)) return v;
    }
  }
  return undefined;
}

/** Build an EUI-driven W/m² for a known landmark — overrides INTENSITY_W_PER_M2. */
function intensityForLandmark(lm: PratumwanLandmarkRecord): number {
  // Use the per-landmark EUI directly (it's already real-world).
  return Math.round((lm.euiKwhM2Yr / HOURS_PER_YEAR) * 1000);
}

/** Smarter `intensityFor` — checks the Pratumwan category before falling back. */
function intensityForWithPratumwan(
  buildingType: string,
  landmark?: PratumwanLandmarkRecord,
): number {
  if (landmark) return intensityForLandmark(landmark);
  // OSM tag → Pratumwan category mapping
  const ptHit = PRATUMWAN_AVG_W_PER_M2[buildingType];
  if (ptHit != null) return ptHit;
  return intensityFor(buildingType);
}

// Spherical-polygon area in m². Roughly accurate at Bangkok latitudes
// (good to ~0.1%); we don't need cartographic precision for an energy
// model.
function ringAreaM2(ring: number[][]): number {
  if (ring.length < 3) return 0;
  const R = 6378137; // earth radius m
  let area = 0;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const [lng1, lat1] = ring[j];
    const [lng2, lat2] = ring[i];
    area += ((lng2 - lng1) * Math.PI / 180) * (2 + Math.sin(lat1 * Math.PI / 180) + Math.sin(lat2 * Math.PI / 180));
  }
  return Math.abs(area * R * R / 2);
}

function polygonAreaM2(geom: Polygon | MultiPolygon): number {
  if (geom.type === "Polygon") {
    return ringAreaM2(geom.coordinates[0]);
  }
  return geom.coordinates.reduce((sum, poly) => sum + ringAreaM2(poly[0]), 0);
}

/**
 * Diurnal multiplier: 0.3 night, ramp up to 1.0 between 09:00-16:00,
 * easing back to 0.55 by 22:00. Matches an academic-campus profile —
 * hospital buildings flatten this out (mostly 24/7).
 */
function hourMultiplier(hourLocal: number, type: string): number {
  if (type === "hospital") {
    // 24/7 ops — narrower range, peak mid-day (rounds + OR + diagnostics)
    if (hourLocal < 5) return 0.75;
    if (hourLocal < 8) return 0.88;
    if (hourLocal < 18) return 1.0;
    if (hourLocal < 22) return 0.93;
    return 0.78;
  }
  if (type === "mall") {
    // Closed before 10:00, open 10:00–22:00, peak 17:00–21:00 (after-work crowd)
    if (hourLocal < 7) return 0.18;     // overnight refrigeration + standby
    if (hourLocal < 10) return 0.32;    // staff arrives, AC ramps
    if (hourLocal < 12) return 0.72;
    if (hourLocal < 17) return 0.88;
    if (hourLocal < 22) return 1.0;     // peak shopping
    if (hourLocal < 24) return 0.45;    // closing down
    return 0.18;
  }
  if (type === "hotel") {
    // 24/7 occupancy but two peaks: morning (showers + breakfast) and evening (HVAC + dining)
    if (hourLocal < 6) return 0.55;
    if (hourLocal < 9) return 0.90;     // morning shower / breakfast surge
    if (hourLocal < 12) return 0.72;
    if (hourLocal < 17) return 0.80;
    if (hourLocal < 21) return 1.0;     // evening
    if (hourLocal < 23) return 0.85;
    return 0.62;
  }
  if (type === "condo" || type === "residential") {
    // Residential: morning + evening peaks, low mid-day
    if (hourLocal < 5) return 0.32;
    if (hourLocal < 8) return 0.78;     // wake / shower
    if (hourLocal < 17) return 0.40;    // mostly empty
    if (hourLocal < 21) return 0.92;    // return home + cook
    if (hourLocal < 23) return 0.85;
    return 0.50;
  }
  // Academic / office / commercial pattern
  if (hourLocal < 5) return 0.28;
  if (hourLocal < 7) return 0.40;
  if (hourLocal < 9) return 0.72;
  if (hourLocal < 12) return 0.95;
  if (hourLocal < 14) return 1.0;     // peak air-con + classes
  if (hourLocal < 17) return 0.97;
  if (hourLocal < 19) return 0.78;
  if (hourLocal < 22) return 0.56;
  return 0.35;
}

function dayMultiplier(dayOfWeek: number, type: string): number {
  // Sun=0 Sat=6 in JS; Bangkok academic weekend Sat-Sun
  if (type === "hospital") return 0.96;  // very flat
  if (type === "mall") {
    // Malls are BUSIER on weekends — Bangkok mall traffic spikes Sat afternoon
    return (dayOfWeek === 0 || dayOfWeek === 6) ? 1.15 : 1.0;
  }
  if (type === "hotel") return 1.0;       // year-round occupancy
  if (type === "condo" || type === "residential") {
    return (dayOfWeek === 0 || dayOfWeek === 6) ? 1.10 : 0.95;
  }
  return (dayOfWeek === 0 || dayOfWeek === 6) ? 0.55 : 1.0;
}

/** Deterministic 0..1 hash from a string seed — Mulberry32-style. */
function seedNoise(seed: string, salt: number): number {
  let h = 2166136261 ^ salt;
  for (let i = 0; i < seed.length; i++) {
    h = Math.imul(h ^ seed.charCodeAt(i), 16777619);
  }
  h += h << 13; h ^= h >>> 7;
  h += h << 3;  h ^= h >>> 17;
  h += h << 5;
  return ((h >>> 0) / 4294967295);
}

export interface ReadingOpts {
  /** Override "now" — useful for time-travel / testing. */
  at?: Date;
  /** Bangkok-local hour to use instead of computing from `at`. */
  hourLocal?: number;
  /** Pratumwan landmark match — overrides intensity with the real EUI. */
  landmark?: PratumwanLandmarkRecord;
  /** GFA override (m²) — used by landmark readings instead of footprint × floors. */
  gfaOverrideM2?: number;
}

/**
 * Compute a single building's energy reading. Pure function: same inputs
 * always produce the same output, modulo the time-bucket (refreshes every
 * 5 min so the heatmap visibly animates without flicker).
 */
export function readingFor(
  feature: Feature<Polygon | MultiPolygon, BuildingProperties>,
  opts: ReadingOpts = {},
): BuildingEnergyReading {
  const p = feature.properties ?? {};
  const id = p.id ?? `b-${feature.id ?? Math.random().toString(36).slice(2, 9)}`;
  const name = p.nameEn ?? p.name ?? p.nameTh ?? null;
  const buildingType = (p.building ?? "yes").toLowerCase();

  const floors = Math.max(1, p.levels ?? (p.height ? Math.round(p.height / 3.2) : 1));
  const footprintM2 = polygonAreaM2(feature.geometry);
  const floorAreaM2 = opts.gfaOverrideM2 != null
    ? Math.round(opts.gfaOverrideM2)
    : Math.round(footprintM2 * floors);

  const now = opts.at ?? new Date();
  // Floor "now" to a 5-minute bucket so the same building keeps a stable
  // value across renders within that window — heatmap animates every 5 min.
  const bucket = Math.floor(now.getTime() / (5 * 60_000));
  const hourLocal = opts.hourLocal
    ?? Number(new Intl.DateTimeFormat("en-GB", { timeZone: "Asia/Bangkok", hour: "2-digit", hour12: false }).format(now));
  const dayOfWeek = new Date(now.toLocaleString("en-US", { timeZone: "Asia/Bangkok" })).getDay();

  // If a landmark match is provided, use its category to drive both the
  // intensity AND the diurnal curve (a mall doesn't have an "academic"
  // curve — it peaks 11:00–21:00; a hospital is flat).
  const curveType = opts.landmark?.category ?? buildingType;
  const intensity = intensityForWithPratumwan(buildingType, opts.landmark);
  const hourMul = hourMultiplier(hourLocal, curveType);
  const dayMul = dayMultiplier(dayOfWeek, curveType);

  // Building-specific deterministic noise: per-building character — some
  // run hotter than others — but stable inside a bucket.
  const character = 0.85 + seedNoise(id, 0) * 0.30;        // 0.85..1.15
  const bucketJitter = 0.95 + seedNoise(id, bucket) * 0.10; // 0.95..1.05

  const baseKw = (floorAreaM2 * intensity) / 1000;
  let currentKw = baseKw * hourMul * dayMul * character * bucketJitter;

  // ~0.7% anomalies per bucket — stable for the 5-min window so an admin
  // could actually click into it.
  const anomalyRoll = seedNoise(id, bucket * 31 + 7);
  let anomalyScore = 0;
  const flags: string[] = [];
  if (anomalyRoll > 0.993) {
    // Stuck-AC class anomaly: 2.5× normal load
    currentKw *= 2.5;
    anomalyScore = 0.95;
    flags.push("stuck-cooling");
  } else if (anomalyRoll > 0.985) {
    currentKw *= 1.8;
    anomalyScore = 0.78;
    flags.push("elevated");
  } else if (anomalyRoll < 0.007) {
    // Reverse anomaly: building "should" be lit, isn't. Possible outage.
    currentKw *= 0.15;
    anomalyScore = 0.85;
    flags.push("possible-outage");
  } else if (hourLocal >= 23 || hourLocal < 5) {
    if (currentKw > baseKw * 0.5 && buildingType !== "hospital") {
      flags.push("after-hours-load");
    }
  }

  // Daily kWh = rough integral over the diurnal curve given baseKw.
  // A typical academic building runs ~70% of peak average across 24h.
  const dailyKwh = baseKw * 24 * (buildingType === "hospital" ? 0.90 : 0.55) * character;

  const vsAvg = baseKw > 0 ? currentKw / baseKw : 1;

  return {
    buildingId: id,
    name,
    buildingType,
    floors,
    floorAreaM2,
    currentKw: Math.round(currentKw * 10) / 10,
    dailyKwh: Math.round(dailyKwh),
    vsAvg: Math.round(vsAvg * 100) / 100,
    anomalyScore: Math.round(anomalyScore * 100) / 100,
    flags,
  };
}

export interface CampusEnergySnapshot {
  totalKw: number;
  totalDailyKwh: number;
  buildingCount: number;
  anomalyCount: number;
  topConsumers: BuildingEnergyReading[];
  anomalies: BuildingEnergyReading[];
  /** Estimated solar PV output right now (kW). Synthetic until MEA feed. */
  solarKw: number;
  /** Net grid import (kW) = totalKw - solarKw. */
  netGridKw: number;
  // ── Carbon ledger ─────────────────────────────────────────────────────
  /** Daily gross emissions from grid electricity (kg CO₂). */
  carbonGridKgPerDay: number;
  /** Daily emissions avoided via solar PV (kg CO₂). */
  carbonSolarAvoidedKgPerDay: number;
  /** Daily emissions sequestered by documented trees (kg CO₂). */
  carbonTreesAbsorbedKgPerDay: number;
  /** Net daily emissions = grid − solar − trees (kg CO₂). */
  carbonNetKgPerDay: number;
  /** % of total emissions offset by solar + trees combined. 100 = neutral. */
  carbonNeutralityPct: number;
  computedAt: string;
}

/** Thai grid emission factor — EGAT 2025 average. */
const GRID_KG_CO2_PER_KWH = 0.428;
/** Mature tropical urban tree, mid-range estimate. */
const TREE_KG_CO2_PER_YEAR = 30;

/** Aggregate every building reading into one campus-level snapshot. */
export function campusSnapshot(
  readings: BuildingEnergyReading[],
  options: { treeCount?: number; at?: Date } = {},
): CampusEnergySnapshot {
  const at = options.at ?? new Date();
  const treeCount = options.treeCount ?? 0;
  const totalKw = readings.reduce((s, r) => s + r.currentKw, 0);
  const totalDailyKwh = readings.reduce((s, r) => s + r.dailyKwh, 0);
  const anomalies = readings.filter((r) => r.anomalyScore >= 0.7);
  // Solar PV: ~20 MW CU-MEA microgrid target, ramps with daylight.
  const hourLocal = Number(new Intl.DateTimeFormat("en-GB", { timeZone: "Asia/Bangkok", hour: "2-digit", hour12: false }).format(at));
  const solarRamp =
    hourLocal < 6 || hourLocal > 19 ? 0 :
    hourLocal === 6 || hourLocal === 19 ? 0.15 :
    hourLocal === 7 || hourLocal === 18 ? 0.45 :
    hourLocal === 8 || hourLocal === 17 ? 0.78 :
    1.0;
  // Current installed PV ~5.4 MW (real CU figure), target 20 MW
  const solarKw = Math.round(5400 * solarRamp);
  const topConsumers = [...readings].sort((a, b) => b.currentKw - a.currentKw).slice(0, 8);

  // ── Carbon ledger ────────────────────────────────────────────────────
  // Grid emissions: every kWh imported from the Thai grid carries
  // 0.4787 kg CO₂ (EGAT system avg). Solar offsets at the same intensity
  // because each kWh of PV is a kWh of grid NOT imported.
  // Daily solar = peak × effective sun-hours (Bangkok ≈5 h/day clear).
  const solarDailyKwh = 5400 * 5;             // 27,000 kWh/day @ 5.4 MW
  const gridDailyKwh = Math.max(0, totalDailyKwh - solarDailyKwh);
  const carbonGridKgPerDay = Math.round(gridDailyKwh * GRID_KG_CO2_PER_KWH);
  const carbonSolarAvoidedKgPerDay = Math.round(solarDailyKwh * GRID_KG_CO2_PER_KWH);
  const carbonTreesAbsorbedKgPerDay = Math.round((treeCount * TREE_KG_CO2_PER_YEAR) / 365);
  const carbonNetKgPerDay = carbonGridKgPerDay - carbonTreesAbsorbedKgPerDay;
  const grossDayKg = totalDailyKwh * GRID_KG_CO2_PER_KWH;
  const offsetDayKg = carbonSolarAvoidedKgPerDay + carbonTreesAbsorbedKgPerDay;
  const carbonNeutralityPct = grossDayKg > 0 ? Math.min(100, Math.round((offsetDayKg / grossDayKg) * 100)) : 0;

  return {
    totalKw: Math.round(totalKw),
    totalDailyKwh: Math.round(totalDailyKwh),
    buildingCount: readings.length,
    anomalyCount: anomalies.length,
    topConsumers,
    anomalies: anomalies.sort((a, b) => b.anomalyScore - a.anomalyScore).slice(0, 6),
    solarKw,
    netGridKw: Math.round(totalKw - solarKw),
    carbonGridKgPerDay,
    carbonSolarAvoidedKgPerDay,
    carbonTreesAbsorbedKgPerDay,
    carbonNetKgPerDay,
    carbonNeutralityPct,
    computedAt: at.toISOString(),
  };
}

// ═══════════════════════════════════════════════════════════════════════════
// PRATUMWAN — district-wide energy aggregation
// ═══════════════════════════════════════════════════════════════════════════

export interface PratumwanLandmarkReading extends BuildingEnergyReading {
  landmarkId: string;
  landmarkName: string;
  category: PratumwanLandmarkRecord["category"];
  euiKwhM2Yr: number;
}

export interface PratumwanSectorTotal {
  category: PratumwanLandmarkRecord["category"];
  currentMw: number;
  dailyMwh: number;
  buildingCount: number;
}

export interface PratumwanSnapshot {
  totalMw: number;
  totalDailyMwh: number;
  buildingCount: number;
  sectors: PratumwanSectorTotal[];
  topConsumers: PratumwanLandmarkReading[];
  /** Estimated CO₂ output rate now (kg/h). */
  carbonKgPerHour: number;
  /** Daily emissions (tonnes). */
  carbonDailyTonnes: number;
  computedAt: string;
}

/**
 * Build a fast name-keyed index from a landmarks FeatureCollection.
 * Use `lookupLandmark(feature, index)` to match any building feature
 * back to known landmarks at ~O(1) per building.
 */
export function buildLandmarkIndex(
  features: Array<{ properties: PratumwanLandmarkRecord }>,
): Map<string, PratumwanLandmarkRecord> {
  const idx = new Map<string, PratumwanLandmarkRecord>();
  for (const f of features) {
    if (!f.properties) continue;
    const name = f.properties.name?.toLowerCase().trim();
    const nameTh = f.properties.nameTh?.toLowerCase().trim();
    if (name) idx.set(name, f.properties);
    if (nameTh) idx.set(nameTh, f.properties);
  }
  return idx;
}

/**
 * Compute per-landmark readings using the landmark's real EUI + GFA,
 * then aggregate the district-wide totals by sector.
 */
export function pratumwanSnapshot(
  landmarks: PratumwanLandmarkRecord[],
  opts: { at?: Date } = {},
): { snapshot: PratumwanSnapshot; landmarkReadings: PratumwanLandmarkReading[] } {
  const at = opts.at ?? new Date();
  const hourLocal = Number(
    new Intl.DateTimeFormat("en-GB", { timeZone: "Asia/Bangkok", hour: "2-digit", hour12: false }).format(at),
  );
  const dayOfWeek = new Date(at.toLocaleString("en-US", { timeZone: "Asia/Bangkok" })).getDay();

  const landmarkReadings: PratumwanLandmarkReading[] = landmarks.map((lm) => {
    const intensity = intensityForLandmark(lm);
    const hourMul = hourMultiplier(hourLocal, lm.category);
    const dayMul = dayMultiplier(dayOfWeek, lm.category);
    const character = 0.92 + seedNoise(lm.id, 0) * 0.16;
    const bucket = Math.floor(at.getTime() / (5 * 60_000));
    const bucketJitter = 0.96 + seedNoise(lm.id, bucket) * 0.08;
    const baseKw = (lm.gfaM2 * intensity) / 1000;
    const currentKw = baseKw * hourMul * dayMul * character * bucketJitter;
    // Daily integral — sum the 24 hourly multipliers for the category to get
    // the load factor, then daily kWh = baseKw × loadFactor × 24
    let dayMulSum = 0;
    for (let h = 0; h < 24; h++) dayMulSum += hourMultiplier(h, lm.category);
    const loadFactor = dayMulSum / 24;
    const dailyKwh = baseKw * 24 * loadFactor * dayMul * character;
    return {
      buildingId: lm.id,
      landmarkId: lm.id,
      landmarkName: lm.name,
      category: lm.category,
      euiKwhM2Yr: lm.euiKwhM2Yr,
      name: lm.name,
      buildingType: lm.category,
      floors: Math.max(1, Math.round(lm.heightM / 3.5)),
      floorAreaM2: lm.gfaM2,
      currentKw: Math.round(currentKw),
      dailyKwh: Math.round(dailyKwh),
      vsAvg: baseKw > 0 ? Math.round((currentKw / baseKw) * 100) / 100 : 1,
      anomalyScore: 0,
      flags: [],
    };
  });

  // Sector aggregation
  const sectorMap = new Map<PratumwanLandmarkRecord["category"], PratumwanSectorTotal>();
  for (const r of landmarkReadings) {
    const entry = sectorMap.get(r.category) ?? { category: r.category, currentMw: 0, dailyMwh: 0, buildingCount: 0 };
    entry.currentMw += r.currentKw / 1000;
    entry.dailyMwh += r.dailyKwh / 1000;
    entry.buildingCount += 1;
    sectorMap.set(r.category, entry);
  }
  const sectors = Array.from(sectorMap.values()).map((s) => ({
    ...s,
    currentMw: Math.round(s.currentMw * 10) / 10,
    dailyMwh: Math.round(s.dailyMwh),
  })).sort((a, b) => b.currentMw - a.currentMw);

  const totalKw = landmarkReadings.reduce((s, r) => s + r.currentKw, 0);
  const totalDailyKwh = landmarkReadings.reduce((s, r) => s + r.dailyKwh, 0);
  const topConsumers = [...landmarkReadings].sort((a, b) => b.currentKw - a.currentKw).slice(0, 8);

  return {
    snapshot: {
      totalMw: Math.round((totalKw / 1000) * 10) / 10,
      totalDailyMwh: Math.round(totalDailyKwh / 1000),
      buildingCount: landmarkReadings.length,
      sectors,
      topConsumers,
      carbonKgPerHour: Math.round(totalKw * GRID_KG_CO2_PER_KWH),
      carbonDailyTonnes: Math.round((totalDailyKwh * GRID_KG_CO2_PER_KWH) / 1000),
      computedAt: at.toISOString(),
    },
    landmarkReadings,
  };
}
